import numeral from 'numeral';
import type { JSX } from 'react';
import React from 'react';
import { Trans, useTranslation } from 'react-i18next';

import type { IEmailABTest } from '@feathr/blackbox';
import { EMAIL_AB_TEST_VARIATIONS } from '@feathr/blackbox';
import { CardV2 as Card, EmptyState, Text } from '@feathr/components';
import { getEmailABTestVersionLabel } from '@feathr/extender/utils/emailABTest';

import type { IABTestReportStats } from './ABTestReport.types';
import ABTestSummaryVersion from './ABTestSummaryVersion';

import * as styles from './ABTestSummary.css';

interface IProps {
  abTest: IEmailABTest;
  summary?: IABTestReportStats['summary'];
}

function ABTestSummary({ abTest, summary }: Readonly<IProps>): JSX.Element {
  const { t } = useTranslation();

  if (!summary) {
    return (
      <Card width={'full'}>
        <Card.Header title={t('Test summary')} />
        <Card.Content>
          <EmptyState label={t('No data available')} theme={'white'} width={'full'} />
        </Card.Content>
      </Card>
    );
  }

  const totalSent = EMAIL_AB_TEST_VARIATIONS.reduce(
    (total, variation) => total + summary[variation].sent,
    0,
  );

  return (
    <Card width={'full'}>
      <Card.Header title={t('Test summary')} />
      <Card.Content contentClassName={styles.content}>
        <div className={styles.totals}>
          <Text.DefaultRegular>
            <Trans t={t}>
              Total emails sent: <strong>{{ total: numeral(totalSent).format('0,0') }}</strong>
            </Trans>
          </Text.DefaultRegular>
          <div className={styles.sent}>
            {EMAIL_AB_TEST_VARIATIONS.map((variation) => (
              <Text.SmallRegular key={variation}>
                <Trans count={summary[variation].sent} t={t}>
                  <strong>{{ version: getEmailABTestVersionLabel(variation, t) }}</strong> sent{' '}
                  {{ count: summary[variation].sent }} emails
                </Trans>
              </Text.SmallRegular>
            ))}
          </div>
        </div>
        {EMAIL_AB_TEST_VARIATIONS.map((variation) => (
          <ABTestSummaryVersion
            abTest={abTest}
            key={variation}
            summary={summary[variation]}
            variation={variation}
          />
        ))}
      </Card.Content>
    </Card>
  );
}

export default ABTestSummary;
